import * as Sentry from '@sentry/react'

import { updateGeolocation, updateGeolocationError } from '../root/reducers/shipments/geolocation'
import { PERMISSION_ERRORS, PERMISSION_STATES } from './permissions-service'

const defaultPosition = {
  lat: null,
  lng: null
}

const geolocationValues = {
  newPermission: PERMISSION_STATES.PROMPT,
  geoPosition: {}, // this will contain all geolocation information
  position: defaultPosition, // this only contains the latitude and longitude
  positionErrorMsg: '',
  watchId: null
}

const updateAndSaveGeolocation = (dispatch, geolocation, callback) => {
  dispatch(updateGeolocation(geolocation))
  callback(geolocation)
}

const handlePermissionGranted = (resolve, dispatch) => (newPosition) => {
  const { latitude, longitude, accuracy } = newPosition.coords
  const breadcrumbData = {
    lat: latitude,
    lng: longitude,
    accuracy: accuracy != null ? `${Math.round(accuracy)}m` : 'unknown',
    timestamp: new Date(newPosition.timestamp).toISOString()
  }
  console.debug('[GPS] Position obtained', breadcrumbData)
  Sentry.addBreadcrumb({
    category: 'gps',
    message: 'Position obtained',
    data: breadcrumbData,
    level: 'info'
  })

  const newGeolocation = {
    ...geolocationValues,
    newPermission: PERMISSION_STATES.GRANTED,
    geoPosition: {
      coords: {
        latitude,
        longitude,
        accuracy,
        altitude: newPosition.coords.altitude,
        altitudeAccuracy: newPosition.coords.altitudeAccuracy,
        heading: newPosition.coords.heading,
        speed: newPosition.coords.speed
      },
      timestamp: newPosition.timestamp
    },
    position: {
      lat: latitude,
      lng: longitude
    }
  }

  updateAndSaveGeolocation(dispatch, newGeolocation, resolve)
}

const handlePermissionDenied = (reject, dispatch) => (error) => {
  switch (error.code) {
    case error.PERMISSION_DENIED: {
      const values = {
        ...geolocationValues,
        newPermission: PERMISSION_STATES.DENIED,
        positionErrorMsg: PERMISSION_ERRORS.PERMISSION_DENIED
      }
      updateAndSaveGeolocation(dispatch, values, reject)
      break
    }

    // Transient failures — keep existing position, only flag the error message.
    // These are not permission denials; the user may still be able to get a fix.
    case error.POSITION_UNAVAILABLE:
      dispatch(updateGeolocationError({ positionErrorMsg: PERMISSION_ERRORS.POSITION_UNAVAILABLE }))
      reject(new Error(PERMISSION_ERRORS.POSITION_UNAVAILABLE))
      break

    case error.TIMEOUT:
      dispatch(updateGeolocationError({ positionErrorMsg: PERMISSION_ERRORS.TIMEOUT }))
      reject(new Error(PERMISSION_ERRORS.TIMEOUT))
      break

    default: {
      const values = {
        ...geolocationValues,
        newPermission: PERMISSION_STATES.DENIED,
        positionErrorMsg: PERMISSION_ERRORS.DEFAULT
      }
      updateAndSaveGeolocation(dispatch, values, reject)
      break
    }
  }
}

const defaultConfigOptions = {
  // Finite timeout: with Infinity the watcher hangs silently when the device
  // never delivers a fix, leaving position as {null, null} indefinitely.
  timeout: 30000,
  maximumAge: 0,
  enableHighAccuracy: true
}

// The config options will overide the default config if the values are given
const getGeolocationArguments = ({ resolve, reject, dispatch, configOptions }) => {
  const getPermissionGranted = () => handlePermissionGranted(resolve, dispatch)
  const getPermissionDenied = () => handlePermissionDenied(reject, dispatch)
  const geolocationConfig = {
    ...defaultConfigOptions,
    ...configOptions
  }

  return [
    getPermissionGranted(),
    getPermissionDenied(),
    geolocationConfig
  ]
}

const handleGetGeolocation = (permission, dispatch, configOptions) => {
  const geolocation = navigator.geolocation
  const isPermissionPrompt = permission === PERMISSION_STATES.PROMPT

  return new Promise((resolve, reject) => {
    if (isPermissionPrompt) {
      geolocation.getCurrentPosition(
        ...getGeolocationArguments({ resolve, reject, dispatch, configOptions })
      )
    } else {
      geolocationValues.watchId = geolocation.watchPosition(
        ...getGeolocationArguments({ resolve, reject, dispatch, configOptions })
      )
    }
  })
}

const getGeolocation = async (permission, dispatch, configOptions = {}) => {
  try {
    const result = await handleGetGeolocation(permission, dispatch, configOptions)
    return result
  } catch (error) {
    throw new Error('New geolocation permission error', { cause: error })
  }
}

const clearGeolocationWatch = (id) => {
  if (id) {
    navigator.geolocation.clearWatch(id)
    return geolocationValues
  }
}

// Forces a fresh single GPS reading, bypassing any cached position.
// Use this for retry scenarios when the initial fix may have been stale or inaccurate.
const retryGeolocation = (dispatch) => {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      handlePermissionGranted(resolve, dispatch),
      handlePermissionDenied(reject, dispatch),
      { maximumAge: 0, timeout: 10000, enableHighAccuracy: true }
    )
  })
}

export {
  getGeolocation,
  clearGeolocationWatch,
  retryGeolocation
}
